import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Upload, Trash2, Loader2, ChevronUp, ChevronDown, Plus, Star, Video, Image as ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface GalleryImage {
  url: string;
  attribution?: string;
  type?: 'image' | 'video';
  videoId?: string; // YouTube video ID
}

interface ImageGalleryManagerProps {
  images: GalleryImage[];
  onChange: (images: GalleryImage[]) => void;
  storagePath: string; // e.g., "towns/town-id" or "areas/area-id"
  label?: string;
}

// Extract YouTube video ID from various URL formats or iframe code
function extractYouTubeId(input: string): string | null {
  // Check if it's an iframe - extract src
  const iframeMatch = input.match(/src="https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/i);
  if (iframeMatch) return iframeMatch[1];

  // Check various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/i,
    /^([a-zA-Z0-9_-]{11})$/ // Just the ID
  ];

  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (match) return match[1];
  }

  return null;
}

export function ImageGalleryManager({ 
  images, 
  onChange, 
  storagePath,
  label = "Gallery Images"
}: ImageGalleryManagerProps) {
  const { toast } = useToast();
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [videoInput, setVideoInput] = useState('');
  const [videoAttribution, setVideoAttribution] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${storagePath}/gallery_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('town-images')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('town-images')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
      return null;
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingIndex(-1); // -1 indicates adding new

    const newImages: GalleryImage[] = [];
    
    for (const file of Array.from(files)) {
      const url = await uploadImage(file);
      if (url) {
        newImages.push({ url, attribution: '', type: 'image' });
      }
    }

    if (newImages.length > 0) {
      onChange([...images, ...newImages]);
      toast({
        title: `${newImages.length} image(s) added`,
        description: 'Remember to save your changes.',
      });
    }

    setUploadingIndex(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReplaceImage = async (index: number, file: File) => {
    setUploadingIndex(index);

    const url = await uploadImage(file);
    if (url) {
      const updated = [...images];
      updated[index] = { ...updated[index], url, type: 'image', videoId: undefined };
      onChange(updated);
      toast({
        title: 'Image replaced',
        description: 'Remember to save your changes.',
      });
    }

    setUploadingIndex(null);
  };

  const handleAddVideo = () => {
    const videoId = extractYouTubeId(videoInput.trim());
    
    if (!videoId) {
      toast({
        title: 'Invalid YouTube URL',
        description: 'Please enter a valid YouTube URL or iframe embed code.',
        variant: 'destructive',
      });
      return;
    }

    const newVideo: GalleryImage = {
      url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`, // Thumbnail for display
      type: 'video',
      videoId,
      attribution: videoAttribution || '',
    };

    onChange([...images, newVideo]);
    setVideoInput('');
    setVideoAttribution('');
    setShowVideoDialog(false);
    
    toast({
      title: 'Video added',
      description: 'Remember to save your changes.',
    });
  };

  const updateAttribution = (index: number, attribution: string) => {
    const updated = [...images];
    updated[index] = { ...updated[index], attribution };
    onChange(updated);
  };

  const removeImage = (index: number) => {
    const updated = images.filter((_, i) => i !== index);
    onChange(updated);
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    const updated = [...images];
    const [movedItem] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, movedItem);
    onChange(updated);
  };

  // Count images and videos
  const imageCount = images.filter(i => i.type !== 'video').length;
  const videoCount = images.filter(i => i.type === 'video').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <span className="text-xs text-muted-foreground">
          {imageCount} image(s){videoCount > 0 && `, ${videoCount} video(s)`}
        </span>
      </div>

      {/* Image/Video grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {images.map((image, index) => (
          <Card key={index} className="overflow-hidden">
            <div className="relative aspect-video">
              {image.type === 'video' ? (
                <>
                  <img
                    src={image.url}
                    alt={`Video thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {/* Video overlay indicator */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="bg-red-600 text-white rounded-full p-3">
                      <Video className="h-6 w-6" />
                    </div>
                  </div>
                </>
              ) : (
                <img
                  src={image.url}
                  alt={`Gallery image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute top-2 right-2 flex gap-1">
                {image.type !== 'video' && (
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) handleReplaceImage(index, file);
                      };
                      input.click();
                    }}
                    disabled={uploadingIndex === index}
                  >
                    {uploadingIndex === index ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => removeImage(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {/* Reorder controls */}
              <div className="absolute top-2 left-2 flex flex-col gap-1">
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => moveImage(index, index - 1)}
                  disabled={index === 0}
                  title="Move up (make first = hero)"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => moveImage(index, index + 1)}
                  disabled={index === images.length - 1}
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
              {/* Position indicator with hero badge */}
              <div className="absolute bottom-2 left-2 flex items-center gap-1">
                {index === 0 ? (
                  <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                    <Star className="h-3 w-3" /> Hero
                  </span>
                ) : (
                  <span className="bg-background/80 px-2 py-1 rounded text-xs font-medium">
                    {index + 1}
                  </span>
                )}
                {image.type === 'video' && (
                  <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-medium">
                    YouTube
                  </span>
                )}
              </div>
            </div>
            <CardContent className="p-3">
              <Input
                value={image.attribution || ''}
                onChange={(e) => updateAttribution(index, e.target.value)}
                placeholder={image.type === 'video' ? "Video credit (optional)" : "Photo attribution (optional)"}
                className="text-sm"
              />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add buttons */}
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingIndex !== null}
          className="flex-1 h-16 border-dashed"
        >
          {uploadingIndex === -1 ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Uploading...
            </>
          ) : (
            <>
              <ImageIcon className="h-5 w-5 mr-2" />
              Add Images
            </>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => setShowVideoDialog(true)}
          className="flex-1 h-16 border-dashed"
        >
          <Video className="h-5 w-5 mr-2" />
          Add YouTube Video
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Add images or YouTube videos. The first item will be displayed as the hero.
      </p>

      {/* YouTube Video Dialog */}
      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add YouTube Video</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="videoUrl">YouTube URL or Embed Code</Label>
              <Input
                id="videoUrl"
                value={videoInput}
                onChange={(e) => setVideoInput(e.target.value)}
                placeholder="https://youtu.be/... or paste iframe code"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Paste a YouTube URL (youtu.be/xxx or youtube.com/watch?v=xxx) or the full iframe embed code.
              </p>
            </div>
            <div>
              <Label htmlFor="videoAttribution">Video Credit (optional)</Label>
              <Input
                id="videoAttribution"
                value={videoAttribution}
                onChange={(e) => setVideoAttribution(e.target.value)}
                placeholder="e.g., Town of Marion"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVideoDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddVideo} disabled={!videoInput.trim()}>
              Add Video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
